import React from 'react';
import styled from 'react-emotion';
import Dropzone from 'react-dropzone';
import { opacify, darken } from 'polished';

import { BASE_COLOR } from 'shared/constants/colors';

import { mixClasses as cx, mixStyles as sx } from 'helpers';
import injectSheet from 'react-jss';

/*
 * DiamondButton
 */
let DiamondButton = ({
  icon,
  classes: cn,
  outerStyle = {},
  innerStyle = {},
  color = BASE_COLOR.darkGray,
  size = 28,
  onClick,
}) => {
  const outerSize = {width: `${size}px`, height: `${size}px`};
  const innerSize = {width: `${size - 8}px`, height: `${size - 8}px`};

  return (
    <div
      className={cx(cn.outer, '-flex-cc')}
      style={sx(
        outerStyle,
        outerSize,
        { color: opacify(-0.6, color) },
      )}
      onClick={onClick}
    >
      <button
        className={cx(cn.inner, '-flex-cc')}
        style={sx(
          innerStyle,
          innerSize,
          { color },
        )}
      >
        {icon}
      </button>
    </div>
  );
};
DiamondButton = injectSheet({
  outer: {
    transition: 'all 200ms',
    border: '1px solid currentColor',
    transform: 'rotate(-45deg)',
    // opacity: '0.5',
    '&:hover': {
      cursor: 'pointer',
      background: 'currentColor',
    },
  },
  inner: {
    border: 'none',
    outline: 'none',
    background: 'currentColor',
    borderRadius: '3px',
    '&:hover': {
      cursor: 'pointer',
    },
    '& > *': {
      transform: 'rotate(+45deg)',
    }
  },
})(DiamondButton);


/*
 * RotateButton
 */
let RotateButton = ({
  classes: cn,
  icon,
  style = {},
  rotateDegree = 90,
  rotateDirection = '+',
  onClick,
}) => {
  const rotation = `rotate(${rotateDirection}${rotateDegree}deg)`;

  return (
    <div style={style}>
      {/* Dynamically override rotation attributes */}
      <style type="text/css" dangerouslySetInnerHTML={{__html:
        `.${cn.inner}:hover {transform: ${rotation};}`,
      }} />

      <button
        className={cx(cn.inner, '-flex-cc')}
        onClick={onClick}
      >
        {icon}
      </button>
    </div>
  );
};
RotateButton = injectSheet({
  inner: {
    background: 'none',
    border: 'none',
    outline: 'none',
    padding: 0,
    transition: 'transform 300ms cubic-bezier(0.455, 0.03, 0, 1)',
    animation: 'opacityFadeInFull 400ms ease-in 1 forwards',
    '&:hover': {
      cursor: 'pointer',
      transform: 'rotate(+90deg)',  // Default, overridable with props.
      opacity: 1,
    },
  },
})(RotateButton);


/*
 * RectButton
 */
let RectButton = ({
  classes: cn,
  label,
  icon,
  isEnabled = true,     // Clickable state
  isActivated = false,  // Clicked state
  height = 32,
  theme = 'normal',
  className,
  ...props,
}) => {
  const iconFrame = (
    <span className='flex items-center h-100 relative mh2 pl2'>
      <span
        className={cx(
          {[cn.activated]: isActivated},
          '_icon-con absolute flex'
        )}
        style={{transition: 'all 200ms', left: '4px'}}
      >
        {icon}
      </span>
    </span>
  );

  const backdropPulled = icon ? height : 0;
  const backdropActive = `width: ${backdropPulled}px;`;

  const themeCls = {
    'normal': cn.dark,
    'primary': cn.blue,
  }[theme];

  return (
    <button
      disabled={!isEnabled}
      className={cx(
        cn.root,
        themeCls,
        {[cn.enabled]: isEnabled},
        {[cn.disabled]: !isEnabled},
        'moa-ff-sans flex items-center relative bn pa2 ph3 f6 fw3 near-white',
        className,
      )}
      {...props}
    >
      {/* Dynamically override backdrop width attributes */}
      <style type="text/css" dangerouslySetInnerHTML={{__html: `
          .${cn.root}:hover:after     {${backdropActive}}
          .${cn.root}:focus:after     {${backdropActive}}
          .${cn.root}:after           {${isActivated ? backdropActive : ''}}
          .${cn.disabled}:hover:after {width: 100%;}
          .${cn.disabled}:focus:after {width: 100%;}
        `,
      }} />

      {label}
      {icon && iconFrame}
    </button>
  );
};
RectButton = injectSheet({
  root: {
    zIndex: 0,
    outline: 0,
    lineHeight: 1,
    letterSpacing: '1px',
    transition: 'all 200ms',
    background: '#555555',
    '&:before': {
      position: 'absolute',
      transition: 'all 300ms cubic-bezier(0.77, 0, 0.175, 1)',
      zIndex: 0,
      content: '""',
      background: '#555555',
      top: 0,
      left: 0,
      width: '8px',
      height: '100%',
    },
    '&:after': {
      position: 'absolute',
      transition: 'all 300ms cubic-bezier(0.77, 0, 0.175, 1)',
      zIndex: -1,
      content: '""',
      background: BASE_COLOR.black,
      top: 0,
      right: 0,
      width: '100%',
      height: '100%',
    },
  },

  // Color theme.
  dark: {
    '&:after': { background: BASE_COLOR.black },
  },
  blue: {
    '&:after': { background: opacify(-0.1, BASE_COLOR.caretBlue) },
  },

  enabled: {
    '&:hover':  {cursor: 'pointer'},
    '&:hover ._icon-con': {left: '8px !important'},
    '&:focus ._icon-con': {left: '8px !important'},
  },
  disabled: {
    '&:before': {background: '#CCCCCC'},
    '&:hover': {cursor: 'not-allowed'},
    '&:focus': {outlineStyle: 'none'},
    '&:after': {
      // background: '#CCCCCC',
      // background: '#cbc0c0',
      background: '#DAD2D2',  // Light grey with slight red ish hue
    },
  },
  activated: {left: '8px !important'},
})(RectButton);
// TODO: make focus event to trigger hover, and glow style when focus



/*
 * TextButton
 */
let TextButton = ({
  classes: cn,
  style = {},
  className,
  label = null,
  onClick = () => {},
  isActivated = false,
  isDisabled = false,
}) => {

  return (
    <button
      style={style}
      className={cx(cn.button, {
        [cn.activated]: isActivated,
        [cn.disabled]: isDisabled,
      }, 'bn', className)}
      onClick={onClick}
      disabled={isDisabled}
    >
      {label}
    </button>
  );
};
TextButton = injectSheet({
  button: {
    color: BASE_COLOR.moonGray,
    background: 'none',
    outline: 'none',
    transition: 'all 100ms',
    textShadow: 'none',
    '&:hover': {
      cursor: 'pointer',
    },
    '&:hover, &:focus': {
      color: BASE_COLOR.silver,
    },
  },
  activated: {
    color: BASE_COLOR.darkGray,
    '&:hover, &:focus': {
      color: BASE_COLOR.darkGray,
    },
  },
  disabled: {
    color: BASE_COLOR.lightGray,
    // http://css-snippets.com/engraved-text/
    textShadow: '0px 1px 0px rgba(255,255,255,.3), 0px -1px 0px rgba(175,175,175,.7)',
    '&:hover': {
      cursor: 'not-allowed',
    },
    '&:hover, &:focus': {
      color: BASE_COLOR.lightGray,
    },
  },
})(TextButton);



/*
 * GhostButton
 */
let GhostButton = ({
  classes: cn,
  style = {},
  className,
  children,
  onClick = () => {},
  isActivated = false,
  isDisabled = false,
  tabIndex = 0,
  ...props
}) => {

  return (
    <button
      style={style}
      className={cx(
        cn.button,
        {
          [cn.activated]: isActivated,
          [cn.disabled]: isDisabled,
        },
        'moa-ff-sans ba',
        className,
      )}
      onClick={onClick}
      disabled={isDisabled}
      tabIndex={tabIndex}
      {...props}
    >
      {children}
    </button>
  );
};
GhostButton = injectSheet({
  button: {
    color: BASE_COLOR.darkGray,
    outline: 'none',
    borderColor: BASE_COLOR.moonLightGray,
    transition: 'all 200ms',
    background: 'transparent',
    '&:hover, &:focus': {
      color: darken(0.15, BASE_COLOR.caretBlue),
      borderColor: BASE_COLOR.caretBlue,
      cursor: 'pointer',
    },
  },
  activated: {
  },
  disabled: {
    cursor: 'default',
    pointerEvents: 'none',
    color: BASE_COLOR.moonGray,
    background: BASE_COLOR.paperWhite,
  },
})(GhostButton);



/*
 * MeekButton
 *
 * XXX: no longer used..?
 */
const MeekButton = styled('button')(props => ({
  color: props.disabled ? BASE_COLOR.moonGray : BASE_COLOR.darkGray,
  outline: 'none',
  border: 'none',
  transition: 'all 200ms',
  // background: BASE_COLOR.nearWhite,
  // background: BASE_COLOR.paperWhite,
  '&:hover': {
    color: props.disabled ? BASE_COLOR.moonGray : darken(0.1, BASE_COLOR.caretBlue),
    cursor: props.disabled ? 'default' : 'pointer',
    background: BASE_COLOR.nearWhite,
  },
}));



/*
 * DropzoneButton
 */
const DropzoneButton = ({
  label = "Upload",
  style = {},
  className,
  accept,
  multiple = true,
  onDrop
}) => (
  <Dropzone
    className={cx("flex items-center ph2", className)}
    style={{ width: 'auto', height: 'auto', border: 'none', ...style }}
    accept={accept}
    multiple={multiple}
    onDrop={onDrop}
  >
    {label}
  </Dropzone>
);


export {
  DiamondButton,
  RotateButton,
  RectButton,
  TextButton,
  GhostButton,
  MeekButton,
  DropzoneButton,
};



// WEBPACK FOOTER //
// ./src/components/elems/Button.js